import type { RewardOptionCoupon } from '~stores/loyalty-club';

/** A coupon counts toward a region filter when it services at least one of
 * the selected provinces. An empty filter is National, so every coupon
 * counts, matching how every other card on the page treats no selection. */
export const couponsInRegion = (
    coupons: RewardOptionCoupon[],
    provinces: string[]
): RewardOptionCoupon[] =>
    provinces.length === 0
        ? coupons
        : coupons.filter((coupon) =>
              coupon.provinceCodes.some((code) => provinces.includes(code))
          );

export interface CouponStockTotals {
    total: number;
    remaining: number;
}

/**
 * The only correct read of stock: each coupon's own configured `quantity`,
 * and its server-computed `remaining` (quantity minus the claim counter,
 * floored at zero, computed where the claim counter is enforced), summed as
 * served. Never re-derived from `quantity`/`claimedCount` here, so this stays
 * correct if the server's own floor ever grows a nuance these two raw fields
 * don't show. A null value (unlimited quantity, so no cap to sum) is skipped
 * for that total.
 */
export const sumCouponStock = (
    coupons: RewardOptionCoupon[]
): CouponStockTotals => {
    let total = 0;
    let remaining = 0;
    for (const coupon of coupons) {
        if (coupon.quantity != null) total += coupon.quantity;
        if (coupon.remaining != null) remaining += coupon.remaining;
    }
    return { total, remaining };
};
